import React, { useMemo, useState } from "react";
import type { Schedule } from "@/types";
import { isShiftVisibleInCalendar } from "@/utils/schedulerUtils";
import TimelineFilterOverlay from "@/components/overlays/TimelineFilterOverlay";
import FilterButton from "@/components/button/FilterButton";
import CalendarModal from "./CalendarModal.tsx";

interface FilterItem {
    id: string;
    label: string;
}

const OTHER_ITEMS: FilterItem[] = [
    { id: "assigned", label: "Quarts assignés" },
    { id: "toFill", label: "Quarts à combler" },
    { id: "serviceBreak", label: "Bris de service" },
    { id: "leave", label: "Congés" },
    { id: "unavailability", label: "Indisponibilités" },
];

// Shifts are what the planner acts on, so they show up without being asked for;
// leaves and unavailabilities are context, opted into when needed.
export const DEFAULT_CALENDAR_OTHERS = ["assigned", "toFill", "serviceBreak"];

interface GlobalCalendarModalProps {
    open: boolean;
    onClose: () => void;
    schedule: Schedule;
    problematicShiftIds?: string[];
    shiftConstraintBreaches?: { [shiftId: string]: string[] };
    periodStartDate?: string;
    periodEndDate?: string;
    roleItems: FilterItem[];
    employeeItems: FilterItem[];
    selectedRoles: Set<string>;
    selectedEmployees: Set<string>;
    onSelectedRolesChange: (selected: Set<string>) => void;
    onSelectedEmployeesChange: (selected: Set<string>) => void;
    // Owned by SchedulerApp like the two other filters, so the selection outlives the calendar
    // being closed and reopened.
    selectedOthers: Set<string>;
    onSelectedOthersChange: (selected: Set<string>) => void;
    onShiftClick?: (shiftId: string) => void;
}

const GlobalCalendarModal: React.FC<GlobalCalendarModalProps> = (
    {
        open,
        onClose,
        schedule,
        problematicShiftIds,
        shiftConstraintBreaches,
        periodStartDate,
        periodEndDate,
        roleItems,
        employeeItems,
        selectedRoles,
        selectedEmployees,
        onSelectedRolesChange,
        onSelectedEmployeesChange,
        selectedOthers,
        onSelectedOthersChange,
        onShiftClick,
    }) => {
    const [personFilterOpen, setPersonFilterOpen] = useState(false);
    const [roleFilterOpen, setRoleFilterOpen] = useState(false);
    const [otherFilterOpen, setOtherFilterOpen] = useState(false);

    const filteredShifts = useMemo(
        () => schedule.shifts.filter(shift => {
            if (!isShiftVisibleInCalendar(shift, selectedRoles, selectedEmployees)) {
                return false;
            }

            if (shift.employee) {
                return selectedOthers.has("assigned");
            }

            return shift.mandatory === true
                ? selectedOthers.has("serviceBreak")
                : selectedOthers.has("toFill");
        }),
        [schedule.shifts, selectedRoles, selectedEmployees, selectedOthers]
    );

    const availabilityTypes = useMemo(() => {
        const types: ("unavailable" | "undesired")[] = [];

        if (selectedOthers.has("leave")) {
            types.push("unavailable");
        }

        if (selectedOthers.has("unavailability")) {
            types.push("undesired");
        }

        return types;
    }, [selectedOthers]);

    // The role filter is about shifts: a leave belongs to a person, not to a role.
    const availabilityEmployees = useMemo(
        () => availabilityTypes.length === 0
            ? []
            : schedule.employees.filter(employee => selectedEmployees.has(employee.userId)),
        [schedule.employees, selectedEmployees, availabilityTypes]
    );

    const personCount = selectedEmployees.size === employeeItems.length ? 0 : selectedEmployees.size;
    const roleCount = selectedRoles.size === roleItems.length ? 0 : selectedRoles.size;
    const otherCount = selectedOthers.size === OTHER_ITEMS.length ? 0 : selectedOthers.size;

    const headerActions = (
        <div className="flex items-center gap-1">
            <TimelineFilterOverlay
                isOpen={personFilterOpen}
                onOpenChange={setPersonFilterOpen}
                items={employeeItems}
                selectedItems={selectedEmployees}
                onSelectionChange={onSelectedEmployeesChange}
                title="Sélection des employés"
            >
                <FilterButton label="Personne" active={personFilterOpen} count={personCount}/>
            </TimelineFilterOverlay>

            <TimelineFilterOverlay
                isOpen={roleFilterOpen}
                onOpenChange={setRoleFilterOpen}
                items={roleItems}
                selectedItems={selectedRoles}
                onSelectionChange={onSelectedRolesChange}
                title="Sélection des rôles"
            >
                <FilterButton label="Rôle" active={roleFilterOpen} count={roleCount}/>
            </TimelineFilterOverlay>

            <TimelineFilterOverlay
                isOpen={otherFilterOpen}
                onOpenChange={setOtherFilterOpen}
                items={OTHER_ITEMS}
                selectedItems={selectedOthers}
                onSelectionChange={onSelectedOthersChange}
                title="Autres éléments"
            >
                <FilterButton label="Autres" active={otherFilterOpen} count={otherCount}/>
            </TimelineFilterOverlay>
        </div>
    );

    return (
        <CalendarModal
            open={open}
            onClose={onClose}
            title="Calendrier"
            filteredShifts={filteredShifts}
            schedule={schedule}
            problematicShiftIds={problematicShiftIds}
            shiftConstraintBreaches={shiftConstraintBreaches}
            periodStartDate={periodStartDate}
            periodEndDate={periodEndDate}
            badgeContentType="roleEmployee"
            availabilityEmployees={availabilityEmployees}
            availabilityTypes={availabilityTypes}
            headerActions={headerActions}
            emptyWhenNoShifts={availabilityEmployees.length === 0}
            emptyLabel="Aucun quart à afficher."
            highlightRelatedOnHover
            onShiftClick={onShiftClick}
        />
    );
};

export default GlobalCalendarModal;
